import { Dispatch, SetStateAction, useRef } from 'react';
import { HeaderGroup } from 'react-table';

import { TFunction, useTranslation } from '@mint-lib/i18n';
import styled, { css } from '@mint-lib/styled';

import { Button, ButtonProps, Tooltip } from '@myn/mui';

import { Columns } from '../../../../../../constants/myCampaigns.js';
import { VERSION_ROW_TYPES } from '../../../../../../constants/versionsColumnsObject.js';
import { useTableScale } from '../../../../../../hooks/useTableScale.js';
import { useTextOverflow } from '../../../../../../hooks/useTextOverflow.js';

type Props = {
  header: HeaderGroup<object>;
  headerIdx?: number;
  expandedType?: VERSION_ROW_TYPES;
  setExpandedType?: Dispatch<SetStateAction<VERSION_ROW_TYPES>>;
};

const getButtonTextByExpandedType = (
  t: TFunction,
  type?: VERSION_ROW_TYPES,
): {
  expandButtonText?: string;
  expandButtonIcon?: ButtonProps['endIcon'];
} => {
  switch (type) {
    case VERSION_ROW_TYPES.PLATFORM:
      return {
        expandButtonText: t('Collapse all'),
        expandButtonIcon: 'CollapseRows',
      };
    case VERSION_ROW_TYPES.VERSION:
      return {
        expandButtonText: t('Channels'),
        expandButtonIcon: 'ChevronDown',
      };
    case VERSION_ROW_TYPES.CHANNEL:
      return {
        expandButtonText: t('Expand all'),
        expandButtonIcon: 'ExpandRows',
      };
    default:
      return {};
  }
};

const VersionsTableHeadItem = ({
  header,
  expandedType,
  headerIdx,
  setExpandedType,
}: Props) => {
  const { t } = useTranslation('@myn/planning');
  const scale = useTableScale();

  const textElementRef = useRef<HTMLElement | null>(null);

  const isOverflowed = useTextOverflow({ textElementRef });

  const isSticky = header.id === Columns.ACTIONS_MENU;
  const isExpander = header.id === Columns.EXPANDER;

  const { expandButtonText, expandButtonIcon } = getButtonTextByExpandedType(
    t,
    expandedType,
  );

  const handleExpandRows = () => {
    if (expandedType === VERSION_ROW_TYPES.PLATFORM) {
      setExpandedType?.(VERSION_ROW_TYPES.VERSION);
    }

    if (expandedType === VERSION_ROW_TYPES.VERSION) {
      setExpandedType?.(VERSION_ROW_TYPES.CHANNEL);
    }

    if (expandedType === VERSION_ROW_TYPES.CHANNEL) {
      setExpandedType?.(VERSION_ROW_TYPES.PLATFORM);
    }
  };

  return (
    <HeaderColumn
      $scale={scale}
      $width={Number(header.width)}
      $idx={Number(headerIdx)}
      isSticky={isSticky}
      isExpander={isExpander}
      className="th"
    >
      {isExpander ? (
        <Button
          onClick={handleExpandRows}
          variant="text"
          endIcon={expandButtonIcon}
          sx={{ position: 'absolute', right: '20px' }}
        >
          {expandButtonText}
        </Button>
      ) : (
        <Tooltip
          placement="top"
          arrow
          title={header.render?.('Header') ?? ''}
          disableHoverListener={!isOverflowed}
          componentsProps={{ tooltip: { sx: { textTransform: 'uppercase' } } }}
        >
          <StyledHeadText
            $scale={scale}
            $width={Number(header.width)}
            ref={textElementRef}
          >
            {header.render?.('Header')}
          </StyledHeadText>
        </Tooltip>
      )}
    </HeaderColumn>
  );
};

const HeaderColumn = styled('div')<{
  isSticky: boolean;
  isExpander: boolean;
  $scale: number;
  $width: number;
  $idx: number;
}>`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  flex-grow: 1;
  position: sticky !important;
  top: 0;
  ${({ isSticky }) => (!isSticky ? 'left:0;' : '')}
  z-index: ${({ $idx }) => $idx};
  vertical-align: middle;
  user-select: none;
  font-size: 0.875rem;
  padding: 0 ${({ $scale }) => $scale * 16}px;
  text-transform: uppercase;
  ${({ $scale, $width }) => css`
    height: ${40 * $scale}px;
    min-width: ${$width * $scale}px;
    width: 100%;
    max-width: 100%;
  `}

  background-color: ${({ theme }) => theme.palette.background.default};
  border-bottom: 1px solid ${({ theme }) => theme.palette.border.secondary};

  ${({ isSticky }) =>
    isSticky &&
    css`
      right: 0;
      box-shadow: -4px 0px 6px rgba(0, 22, 14, 0.05);
      pointer-events: none;
    `}

  ${({ isExpander }) =>
    isExpander &&
    css`
      border-top-left-radius: 5px;
      &::after {
        content: '';
        position: absolute;
        right: -11px;
        width: 22px;
        height: 22px;
      }
    `}
`;

const StyledHeadText = styled('span')<{
  $scale: number;
  $width: number;
}>`
  padding-bottom: 2px;
  overflow: hidden;
  white-space: nowrap;
  font-weight: 600;
  color: ${({ theme }) => theme.palette.text.primary};
  text-overflow: ellipsis;
  max-width: ${({ $width, $scale }) => css`
    ${$width * $scale}px
  `};
  ${({ $scale }) => css`
    font-size: ${$scale}rem;
    margin-left: ${5 * $scale}px;
  `};
`;

export default VersionsTableHeadItem;
